import _ from 'lodash';
import moment from 'moment';
import { RequestServiceFactory } from './ApiRequestService';
import { LocalStorageUtilities } from '../utilities/StorageUtilities';
import { HTTP_BAD_REQUEST, HTTP_UNAUTHORIZED, isRequestErrorCode } from '../utilities/RequestUtilities';

function SessionService() {
    const requestService = RequestServiceFactory();
    const MUST_BE_ACTIVE_MINUTES = 5;
    const LAST_ACTIVE_KEY = 'lastActive';

    function errorHandler(error) {
        // The default DataService error handler will redirect to the logout handler for 401s.
        // We don't want to do that in SessionService, since it shortcircuits the logic to
        // save the return URL before redirecting.  This error handler just rethrows the error
        // so it gets handled by the SessionStore instead of the default error handler.
        throw error;
    }

    function refreshUserSession() {
        return requestService.post(`/ipm/auth/refresh`, {}, errorHandler);
    }

    function checkToken() {
        return requestService.get(`/ipm/auth/checkToken`, errorHandler);
    }

    function getGroupInfo(groupIds) {
        return requestService.post(`/ipm/v1/admin/groupsProfile/groupAuthorizationInfo`, { groupIds }, errorHandler);
    }

    async function refresh() {
        let userInfo;
        let groupInfo;

        try {
            userInfo = await refreshUserSession();

            // We can fetch other group info here as needed
            groupInfo = await getGroupInfo(_.get(userInfo, 'user.groups', []));

            return {
                userInfo: _.isEmpty(userInfo)
                    ? userInfo
                    : {
                          ...userInfo,
                          user: {
                              ...userInfo.user,
                              id: userInfo.user._id,
                          },
                          groups: {
                              landingPage: _.get(groupInfo, 'landingPage', null),
                              pages: _.get(groupInfo, 'pages', []),
                              allowDrillIn: _.get(groupInfo, 'allowDrillIn'),
                              sites: _.get(groupInfo, 'sites'),
                              businessServices: _.get(groupInfo, 'businessServices'),
                          },
                      },
            };
        } catch (error) {
            if (!isRequestErrorCode(error, [HTTP_BAD_REQUEST, HTTP_UNAUTHORIZED])) {
                // eslint-disable-next-line no-console
                console.error('Unknown token refresh error: ', error);
            }

            throw error;
        }
    }

    /**
     * Pull the last active from local storage.
     */
    function getLastActive() {
        const { lastActive } = LocalStorageUtilities.getJSONValue(LAST_ACTIVE_KEY);
        if (!lastActive) {
            return lastActive;
        }

        return moment(lastActive);
    }

    /**
     * Update the last active time in local storage.
     */
    function updateLastActive() {
        LocalStorageUtilities.setJSONValue(LAST_ACTIVE_KEY, {
            lastActive: moment().valueOf(),
        });
    }

    /**
     * This is for refreshing the JWT, if they have not been active after a certain
     *   amount of time we'll stop refreshing the JWT
     * @returns {boolean|*}
     */
    function isSessionExpired(sessionTimeInMinutes) {
        const lastActive = getLastActive();
        if (!lastActive) {
            return true;
        }

        // Check to see if the user's lastActive time is longer than the allowed session.
        //  If so we'll log them out.
        return lastActive.isBefore(moment().subtract('minutes', sessionTimeInMinutes));
    }

    /**
     * Only used when the system is setup to timeout
     * This is for refreshing the JWT, if they have not been active after a certain
     *   amount of time we'll stop refreshing the JWT
     * @returns {boolean|*}
     */
    function isUserActive() {
        const lastActive = getLastActive();
        if (!lastActive) {
            return false;
        }

        // lastActive is updated in stateChange.config if they haven't changed
        //  state after a certain amount of time we'll say the user is no longer active
        //  and we'll stop updating the JWT
        return lastActive.isAfter(moment().subtract(MUST_BE_ACTIVE_MINUTES, 'minutes'));
    }

    return {
        refresh,
        checkToken,
        isSessionExpired,
        isUserActive,
        updateLastActive,
    };
}

export default SessionService();
